import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L, openContent, TS_CheckboxV2, TS_PropRenderer, TS_Tabs, TS_Toggle } from '@nu-art/thunderstorm-frontend/index';
import { SnippetConfig_InfoMap, SnippetConfig_LabelMap } from '@app/advisor-content-shared';
import { EditorHeader_AdvisorContext } from './EditorHeader_AdvisorContext.js';
import { SearchableItem_AdvisorSnippet } from '../consts.js';
import './Editor_AdvisorSnippet.scss';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { _keys } from '@nu-art/ts-common';
import { ICONS } from '@app/styles';
import { DropDown_NLPModel } from '../../../_entity/nlp-model/ui-components.js';
import { Editor_SnippetFormVariables } from './Editor_SnippetFormVariables/Editor_SnippetFormVariables.js';
import { ModuleFE_NLPModel } from '../../../_entity/nlp-model/index.js';
import { DropDown_ContextRule, ModuleFE_ContextRule } from '@app/hcs-frontend';
import { MultiSelect_FHIRQuery } from '@app/fhir-frontend/_entity/fhir-query/ui-components';
export class Editor_AdvisorSnippet extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        if (nextProps.editMode !== undefined) {
            state.editMode = nextProps.editMode;
        }
        else {
            state.editMode ??= false;
            if (!state.editable.item._id)
                state.editMode = true;
        }
        //Clear empty queryData object
        if (state.editable.item.queryData) {
            if (!state.editable.item.queryData.resourceType)
                state.editable.set('queryData', undefined);
        }
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? 'edit' : 'normal';
    getItemActions = () => {
        const actions = [];
        if (!this.state.editMode)
            actions.push({
                label: 'Edit Snippet',
                action: async () => this.setState({ editMode: true }),
            });
        actions.push({
            label: 'Delete Snippet',
            action: async () => {
                await this.state.editable.delete();
                this.props.deselectSnippet?.();
            }
        });
        return actions;
    };
    getEditModeActions = () => {
        const actions = [];
        //Actions for existing snippet
        if (this.state.editable.item._id)
            actions.push({
                label: 'Done',
                action: async () => this.setState({ editMode: false }),
            });
        //Actions for new snippet
        else {
            if (this.props.deselectSnippet)
                actions.push({
                    label: 'Cancel',
                    action: async () => this.props.deselectSnippet(),
                });
            actions.push({
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                }
            });
        }
        return actions;
    };
    getToolTip = (text) => {
        return openContent.tooltip.top('advisor-context-snippet-tooltip', text, { offset: 10 });
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__advisor-snippet', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(EditorHeader_AdvisorContext, { editable: this.state.editable, searchableItem: SearchableItem_AdvisorSnippet, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions(), editMode: this.state.editMode });
    };
    renderBody = () => {
        const tabs = [
            {
                title: 'Base',
                uid: 'base',
                content: this.renderBase,
            },
            {
                title: 'Advisor Content',
                uid: 'advisor-content',
                content: this.renderAdvisorContent
            },
            {
                title: 'FHIR Query',
                uid: 'fhir-query',
                content: this.renderFHIR,
            }
        ];
        return _jsx(TS_Tabs, { className: 'editor__advisor-snippet__body', tabs: tabs });
    };
    // ######################## Render - Base ########################
    renderBase = () => {
        return _jsxs(_Fragment, { children: [this.renderBase_Label(), this.renderBase_NLPModel(), this.renderBase_ContextRules(), this.renderBase_AllowAISuggestions()] });
    };
    renderBase_Label = () => {
        const snippet = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: 'normal',
                        renderer: () => _jsx("div", { className: 'editor__advisor-snippet__body__value', children: snippet.item.label }),
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(DefaultEditor_InputText, { editable: snippet, prop: 'label' }),
                    }
                ] }) });
    };
    renderBase_NLPModel = () => {
        const snippet = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'NLP Model', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: 'normal',
                        renderer: () => {
                            const model = ModuleFE_NLPModel.cache.unique(snippet.item.nlpModelId);
                            return _jsx("div", { className: 'editor__advisor-snippet__body__value', children: model?.label ?? '-' });
                        },
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(DropDown_NLPModel.editable, { editable: snippet, canUnselect: true, onSelected: item => {
                                return snippet.updateObj({ nlpModelId: item?._id });
                            }, prop: 'nlpModelId' }),
                    }
                ] }) });
    };
    renderBase_ContextRules = () => {
        const snippet = this.state.editable;
        const contextRule = ModuleFE_ContextRule.cache.unique(snippet.item.contextRuleId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Context Rules', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: 'normal',
                        renderer: () => _jsx("div", { className: 'editor__advisor-snippet__body__value', children: contextRule?.label ?? '-' }),
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(DropDown_ContextRule.editable, { editable: snippet, prop: 'contextRuleId' }),
                    }
                ] }) });
    };
    renderBase_AllowAISuggestions = () => {
        const snippet = this.state.editable;
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Allow AI Suggestions', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: 'normal',
                        renderer: () => _jsx("div", { className: 'editor__advisor-snippet__body__value', children: snippet.item.allowAISuggestions ? 'Yes' : 'No' }),
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(TS_CheckboxV2, { label: '', checked: snippet.item.allowAISuggestions, onCheck: check => {
                                snippet.updateObj({ allowAISuggestions: check });
                            } }),
                    }
                ] }) });
    };
    // ######################## Render - Advisor ########################
    renderAdvisorContent = () => {
        return _jsxs(LL_H_C, { className: 'editor__advisor-snippet__advisor-content', children: [_jsxs(LL_V_L, { style: { gap: '20px' }, children: [this.renderAdvisorContent_RenderedLabel(), this.renderAdvisorContent_DefaultText(), this.renderAdvisorContent_Config()] }), this.renderAdvisorContent_FormVariables()] });
    };
    renderAdvisorContent_RenderedLabel = () => {
        const snippet = this.state.editable;
        const label = () => {
            return _jsxs(_Fragment, { children: ["Rendered Label ", _jsx(ICONS.information.component, { style: { cursor: 'help' }, ...this.getToolTip('The label for this snippet in the advisor') })] });
        };
        return _jsx(TS_PropRenderer.Vertical, { label: label, children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: 'normal',
                        renderer: () => _jsx("div", { className: 'editor__advisor-snippet__body__value', children: snippet.item.renderedLabel }),
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: snippet, prop: 'renderedLabel' })
                    }
                ] }) });
    };
    renderAdvisorContent_DefaultText = () => {
        const snippet = this.state.editable;
        const label = () => {
            return _jsxs(_Fragment, { children: ["Default Text ", _jsx(ICONS.information.component, { style: { cursor: 'help' }, ...this.getToolTip('In "free text" mode, the snippet will start with this text') })] });
        };
        return _jsx(TS_PropRenderer.Vertical, { label: label, children: _jsx(DefaultEditor_TextArea, { disabled: !this.state.editMode, editable: snippet, prop: 'defaultText', saveEvent: ['blur'], style: { width: 300, height: 200, resize: 'none' } }) });
    };
    renderAdvisorContent_Config = () => {
        const snippet = this.state.editable;
        const editable = snippet.editProp('config', {});
        return _jsx(TS_PropRenderer.Vertical, { label: 'Config', children: _jsx(LL_V_L, { className: 'editor__advisor-snippet__advisor-content__configs', children: _keys(SnippetConfig_LabelMap).map(key => {
                    const checked = !!editable.item[key];
                    return _jsxs(LL_H_C, { className: 'editor__advisor-snippet__advisor-content__config', children: [_jsx("div", { className: 'editor__advisor-snippet__advisor-content__config__label', children: SnippetConfig_LabelMap[key] }), _jsx(ICONS.information.component, { ...openContent.tooltip.top('snippet-config-tooltip', SnippetConfig_InfoMap[key], { offset: 10 }) }), _jsx(TS_Toggle, { checked: !!editable.item[key], onCheck: () => {
                                    editable.updateObj({ [key]: !checked });
                                }, disabled: !this.state.editMode })] }, key);
                }) }) });
    };
    renderAdvisorContent_FormVariables = () => {
        const snippet = this.state.editable;
        return _jsx(Editor_SnippetFormVariables, { snippet: snippet, editMode: this.state.editMode });
    };
    // ######################## Render - FHIR ########################
    renderFHIR = () => {
        const editable = this.state.editable;
        return _jsx(MultiSelect_FHIRQuery, { editable: editable, prop: 'fhirQueryIds' });
    };
}
//# sourceMappingURL=Editor_AdvisorSnippet.js.map